import { PromoRecurringStatus, PromoStatus } from '@daymade/common-lib/dbEnums';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ZProductSelect } from '../prize';
import { successSchema } from '../utils';
import { ZPrizeOrderProductSelect, ZPrizeOrderSelect } from '../winner/models';
import {
  ZPromoCodeInsert,
  ZPromoCodeWithUsage,
  ZPromoPrizeRewardSelect,
} from './models';

const c = initContract();

export const adminPromoContract = c.router(
  {
    getPromoCodes: {
      method: 'GET',
      path: '/codes',
      responses: {
        200: ZPromoCodeWithUsage.array(),
      },
    },
    addPromoCode: {
      method: 'POST',
      path: '/add-code',
      body: ZPromoCodeInsert,
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    deletePromoCode: {
      method: 'DELETE',
      path: '/delete-code/:code',
      pathParams: z.object({ code: z.string() }),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    updatePromoCodeStatus: {
      method: 'POST',
      path: '/update-status/:code',
      pathParams: z.object({ code: z.string() }),
      body: z.object({ status: z.nativeEnum(PromoStatus) }),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    getPromoRecurringForPlayer: {
      method: 'GET',
      path: '/recurring/:playerId',
      pathParams: z.object({ playerId: z.string() }),
      responses: {
        200: z.array(
          z.object({
            code: z.string(),
            created_at: z.coerce.date(),
            reward_type: z.string(),
            value_per_step: z.number(),
            total_steps: z.number(),
            remaining_steps: z.number(),
            status: z.nativeEnum(PromoRecurringStatus),
          }),
        ),
        404: c.type<{ message: string }>(),
      },
    },
    getPromoPrizeRewardsForPlayer: {
      method: 'GET',
      path: '/prize-rewards/:playerId',
      pathParams: z.object({ playerId: z.string() }),
      responses: {
        200: ZPromoPrizeRewardSelect.pick({
          id: true,
          code: true,
          status: true,
          createdAt: true,
          expiresAt: true,
          chosenAt: true,
          prizeId: true,
        })
          .extend({
            drawId: z.number().nullable(),
            drawName: z.string().nullable(),
            prizeName: z.string().nullable(),
            prizeValue: z.number().nullable(),
            prizeTierCode: z.string().nullable(),
            offerDescription: z.string(),
            assigneeName: z.string().nullable(),
            prizeOrder: ZPrizeOrderSelect.nullable(),
            products: ZPrizeOrderProductSelect.merge(
              ZProductSelect.pick({
                name: true,
                price: true,
                productCode: true,
                discountCode: true,
                purchaseFrom: true,
              }),
            ).array(),
          })
          .array(),
        400: c.type<{ message: string }>(),
      },
    },
    awardPromoPrizeReward: {
      method: 'POST',
      path: '/promo-prize-reward/create',
      body: z.object({
        playerId: z.string(),
        code: z.string(),
      }),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
  },
  {
    pathPrefix: '/admin',
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
    },
  },
);
